import { useEffect, useState } from 'react';
import { ShieldStar, Warning, ChatCircleDots, CreditCard, Gavel, UserCircle, ArrowsClockwise } from '@phosphor-icons/react';
import { API } from '@/App';
import { toast } from 'sonner';

const TONE = {
  GREEN: 'bg-green-100 text-green-700 border-green-200',
  AMBER: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  RED:   'bg-red-100 text-red-700 border-red-200',
};

const RiskPill = ({ level }) => (
  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${TONE[level] || TONE.GREEN}`} data-testid={`risk-${level}`}>{level}</span>
);

const Section = ({ icon: Icon, title, subtitle, children, testid }) => (
  <section className="bg-white rounded-2xl border border-gray-200 p-6 mb-6" data-testid={testid}>
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-lg dropio-gradient flex items-center justify-center">
        <Icon size={20} weight="fill" className="text-white" />
      </div>
      <div>
        <h2 className="font-bold text-gray-900">{title}</h2>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
    </div>
    {children}
  </section>
);

const Stat = ({ label, value, tone = 'gray' }) => {
  const t = { gray: 'text-gray-900', red: 'text-red-600', amber: 'text-yellow-700', green: 'text-green-600' }[tone];
  return (
    <div className="bg-gray-50 rounded-lg p-3" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <p className={`text-2xl font-extrabold ${t}`} style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{value ?? '—'}</p>
      <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mt-1">{label}</p>
    </div>
  );
};

const TrustSafety = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actingOn, setActingOn] = useState(null);

  const load = () => {
    setLoading(true);
    fetch(`${API}/admin/trust-safety`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const act = async (user_id, action) => {
    setActingOn(`${user_id}-${action}`);
    try {
      const res = await fetch(`${API}/admin/users/${user_id}/risk-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action }),
      });
      if (!res.ok) throw new Error();
      toast.success(`User ${action} applied`);
      load();
    } catch {
      toast.error('Action failed');
    } finally {
      setActingOn(null);
    }
  };

  if (loading || !data) return <div className="text-center py-20 text-gray-400" data-testid="ts-loading">Loading trust & safety data…</div>;

  const overall = data.overall_risk || 'GREEN';

  return (
    <div data-testid="trust-safety-page">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="ts-title">
            Trust & Safety
          </h1>
          <p className="text-sm text-gray-500">Operational visibility across fraud, compliance, messaging, payments and disputes.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold text-gray-500">Overall platform risk</span>
          <RiskPill level={overall} />
          <button onClick={load} className="text-gray-400 hover:text-[#6D28D9]" data-testid="ts-refresh">
            <ArrowsClockwise size={18} weight="bold" />
          </button>
        </div>
      </div>
      <p className="text-[11px] text-gray-400 mb-6">Last updated {new Date(data.generated_at).toLocaleString()}</p>

      {/* USERS */}
      <Section icon={UserCircle} title="User Risk Monitoring" subtitle="Top violators, KYC failures, account state" testid="ts-users">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <Stat label="Suspended" value={data.users.suspended} tone={data.users.suspended ? 'red' : 'gray'} />
          <Stat label="Flagged" value={data.users.flagged} tone={data.users.flagged ? 'amber' : 'gray'} />
          <Stat label="KYC Failed" value={data.users.kyc_failed} tone={data.users.kyc_failed ? 'amber' : 'gray'} />
          <Stat label="Multiple KYC Failures" value={data.users.kyc_multi_failed} tone={data.users.kyc_multi_failed ? 'red' : 'gray'} />
        </div>
        <div className="grid lg:grid-cols-2 gap-6">
          <UserRiskTable title="High-risk travelers" rows={data.users.high_risk_travelers} act={act} actingOn={actingOn} testid="travelers-table" />
          <UserRiskTable title="High-risk senders" rows={data.users.high_risk_senders} act={act} actingOn={actingOn} testid="senders-table" />
        </div>
      </Section>

      {/* COMPLIANCE */}
      <Section icon={ShieldStar} title="Compliance Monitoring" subtitle="Blocked parcels, restricted-goods attempts and review queue" testid="ts-compliance">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <Stat label="Blocked (RED)" value={data.compliance.blocked} tone={data.compliance.blocked ? 'red' : 'gray'} />
          <Stat label="AI Flagged (AMBER)" value={data.compliance.ai_flagged_count} tone={data.compliance.ai_flagged_count ? 'amber' : 'gray'} />
          <Stat label="Approved" value={data.compliance.approved} tone="green" />
          <Stat label="Review Queue" value={data.compliance.review_queue.length} tone={data.compliance.review_queue.length ? 'amber' : 'gray'} />
        </div>
        <ParcelTable title="Manual review queue (AMBER)" rows={data.compliance.review_queue} field="compliance_warnings" testid="review-queue" />
        <ParcelTable title="Restricted-goods attempts (RED)" rows={data.compliance.restricted_attempts} field="compliance_violations" testid="restricted-attempts" />
      </Section>

      {/* MESSAGING */}
      <Section icon={ChatCircleDots} title="Messaging Monitoring" subtitle="Bypass attempts blocked by the pre-payment chat scanner" testid="ts-messaging">
        <div className="grid grid-cols-4 md:grid-cols-8 gap-2 mb-5">
          {Object.entries(data.messaging.buckets).map(([k, v]) => (
            <div key={k} className="bg-gray-50 rounded-lg p-2 text-center" data-testid={`msg-bucket-${k}`}>
              <p className="text-lg font-extrabold text-gray-900">{v}</p>
              <p className="text-[10px] font-bold uppercase text-gray-500 capitalize">{k}</p>
            </div>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500">
                <th className="px-3 py-2">User</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Severity</th>
                <th className="px-3 py-2">Blocked content</th>
                <th className="px-3 py-2">When</th>
              </tr>
            </thead>
            <tbody data-testid="messaging-table">
              {data.messaging.recent.length === 0 && (
                <tr><td colSpan="5" className="px-3 py-6 text-center text-gray-400">No bypass attempts logged.</td></tr>
              )}
              {data.messaging.recent.slice(0, 15).map((m, i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="px-3 py-2 text-xs">
                    <div className="font-semibold text-gray-900">{m.user_name}</div>
                    <div className="text-[10px] text-gray-500 font-mono">{m.user_id?.slice(0, 12)}</div>
                  </td>
                  <td className="px-3 py-2 text-xs capitalize">{m.violation_type}</td>
                  <td className="px-3 py-2"><RiskPill level={m.severity} /></td>
                  <td className="px-3 py-2 text-xs text-gray-700 max-w-xs truncate" title={m.blocked_content}>{m.blocked_content}</td>
                  <td className="px-3 py-2 text-[10px] text-gray-400">{m.timestamp && new Date(m.timestamp).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* PAYMENTS */}
      <Section icon={CreditCard} title="Payment Monitoring" subtitle="Escrow exceptions, refunds, anomalies" testid="ts-payments">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat label="Pending Releases" value={data.payments.pending_releases} tone="amber" />
          <Stat label="Full Refunds" value={data.payments.refunds_full} tone={data.payments.refunds_full ? 'amber' : 'gray'} />
          <Stat label="Partial Refunds" value={data.payments.refunds_partial} tone={data.payments.refunds_partial ? 'amber' : 'gray'} />
          <Stat label="Failed Payments" value={data.payments.failed_payments} tone={data.payments.failed_payments ? 'red' : 'gray'} />
          <Stat label="Escrow Exceptions" value={data.payments.escrow_exceptions} tone={data.payments.escrow_exceptions ? 'red' : 'gray'} />
        </div>
      </Section>

      {/* DISPUTES */}
      <Section icon={Gavel} title="Dispute Monitoring" subtitle="Hotspots by user, route and category" testid="ts-disputes">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <Stat label="Open" value={data.disputes.open} tone={data.disputes.open ? 'amber' : 'gray'} />
          <Stat label="Resolved" value={data.disputes.resolved} tone="green" />
          <Stat label="High-dispute Users" value={data.disputes.high_dispute_users.length} tone={data.disputes.high_dispute_users.length ? 'amber' : 'gray'} />
          <Stat label="Routes affected" value={data.disputes.by_route.length} tone="gray" />
        </div>
        <div className="grid lg:grid-cols-2 gap-6">
          <DisputeListTable title="Top users by dispute volume" rows={data.disputes.high_dispute_users} testid="dispute-users-table" />
          <div>
            <h3 className="font-bold text-sm mb-2">Disputes by route</h3>
            <ul className="space-y-1 text-sm" data-testid="disputes-by-route">
              {data.disputes.by_route.length === 0 && <li className="text-gray-400">None.</li>}
              {data.disputes.by_route.map((r, i) => (
                <li key={i} className="flex justify-between bg-gray-50 rounded px-3 py-2">
                  <span>{r.route}</span>
                  <span className="font-bold">{r.count}</span>
                </li>
              ))}
            </ul>
            <h3 className="font-bold text-sm mb-2 mt-4">Disputes by category</h3>
            <ul className="space-y-1 text-sm" data-testid="disputes-by-category">
              {data.disputes.by_category.length === 0 && <li className="text-gray-400">None.</li>}
              {data.disputes.by_category.map((r, i) => (
                <li key={i} className="flex justify-between bg-gray-50 rounded px-3 py-2">
                  <span className="capitalize">{r.category}</span>
                  <span className="font-bold">{r.count}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>
    </div>
  );
};

const UserRiskTable = ({ title, rows, act, actingOn, testid }) => (
  <div data-testid={testid}>
    <h3 className="font-bold text-sm mb-2">{title}</h3>
    {rows.length === 0 ? (
      <p className="text-xs text-gray-400">None flagged.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500">
              <th className="px-2 py-1">User</th>
              <th className="px-2 py-1">Risk</th>
              <th className="px-2 py-1">V</th>
              <th className="px-2 py-1">D</th>
              <th className="px-2 py-1">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.user_id} className="border-t border-gray-100" data-testid={`risk-row-${u.user_id}`}>
                <td className="px-2 py-2 text-xs">
                  <div className="font-semibold">{u.name}</div>
                  <div className="text-[10px] text-gray-500">{u.email}</div>
                </td>
                <td className="px-2 py-2"><RiskPill level={u.risk_level} /></td>
                <td className="px-2 py-2 text-xs font-bold text-center" title="Violations">{u.violations}</td>
                <td className="px-2 py-2 text-xs font-bold text-center" title="Open disputes">{u.disputes_against}</td>
                <td className="px-2 py-2 text-xs">
                  {u.is_suspended ? (
                    <button disabled={actingOn === `${u.user_id}-unsuspend`} onClick={() => act(u.user_id, 'unsuspend')}
                            className="text-green-600 hover:underline font-semibold" data-testid={`unsuspend-${u.user_id}`}>
                      Unsuspend
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button disabled={actingOn === `${u.user_id}-flag`} onClick={() => act(u.user_id, u.is_flagged ? 'unflag' : 'flag')}
                              className="text-yellow-700 hover:underline font-semibold" data-testid={`flag-${u.user_id}`}>
                        {u.is_flagged ? 'Unflag' : 'Flag'}
                      </button>
                      <button disabled={actingOn === `${u.user_id}-suspend`} onClick={() => act(u.user_id, 'suspend')}
                              className="text-red-600 hover:underline font-semibold" data-testid={`suspend-${u.user_id}`}>
                        Suspend
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const ParcelTable = ({ title, rows, field, testid }) => (
  <div className="mt-4" data-testid={testid}>
    <h3 className="font-bold text-xs uppercase tracking-wider text-gray-600 mb-2">{title} ({rows.length})</h3>
    {rows.length === 0 ? (
      <p className="text-xs text-gray-400">Nothing in this queue.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500">
              <th className="px-2 py-1">Sender</th>
              <th className="px-2 py-1">Route</th>
              <th className="px-2 py-1">Category</th>
              <th className="px-2 py-1">Reasons</th>
              <th className="px-2 py-1">When</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 10).map((p) => (
              <tr key={p.parcel_id} className="border-t border-gray-100">
                <td className="px-2 py-2 text-xs">{p.sender_name || p.sender_id?.slice(0, 12)}</td>
                <td className="px-2 py-2 text-xs">{p.origin_country?.toUpperCase()} → {p.destination_country?.toUpperCase()}</td>
                <td className="px-2 py-2 text-xs capitalize">{p.category}</td>
                <td className="px-2 py-2 text-xs text-gray-700 max-w-md">{((p[field] || []).slice(0, 2)).join(' · ') || '—'}</td>
                <td className="px-2 py-2 text-[10px] text-gray-400">{p.created_at && new Date(p.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const DisputeListTable = ({ title, rows, testid }) => (
  <div data-testid={testid}>
    <h3 className="font-bold text-sm mb-2">{title}</h3>
    {rows.length === 0 ? (
      <p className="text-xs text-gray-400">No repeat users.</p>
    ) : (
      <ul className="space-y-1 text-sm">
        {rows.map((u) => (
          <li key={u.user_id} className="flex justify-between bg-gray-50 rounded px-3 py-2">
            <span><strong>{u.name}</strong> <span className="text-[10px] text-gray-500 capitalize">{u.user_type}</span></span>
            <span className="font-bold">{u.dispute_count}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default TrustSafety;
